import './auth.scss';

import classNames from 'classnames';
import { Suspense, lazy, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES } from '@/shared/routes';
import MainLoader from '@/shared/ui/main-loader';
import { useAppSelector } from '@/store';

import { closeAuth, setAuthMode } from './auth-overlay';
import type { IAuthPhoneState } from './services/auth-state';
import { AuthFormError } from './ui';

const Login = lazy(() =>
  import('./Login/Login').then((m) => ({ default: m.Login }))
);
const Registration = lazy(() =>
  import('./Registration/Registration').then((m) => ({
    default: m.Registration
  }))
);

/**
 * Полноэкранный оверлей авторизации/регистрации поверх текущего контента.
 * Состояние (open/mode) — в Redux (store/auth.slice); переключение login ↔ register
 * через setAuthMode (без навигации и без рекурсии).
 * Закрытие: бэкдроп, крестик, Esc и кнопка «назад» браузера (= системный «назад» на мобиле).
 * Успешная авторизация закрывает с результатом true.
 */
const AuthOverlay: React.FC = () => {
  const open = useAppSelector((state) => state.auth.overlay.open);
  const mode = useAppSelector((state) => state.auth.overlay.mode);
  const { navigate } = useAppNavigate();

  useEffect(() => {
    if (!open) return;

    // Добавляем запись в history, чтобы «назад» закрывал оверлей, а не уводил с маршрута
    // (URL не меняем — pushState без url; запись помечаем флагом).
    window.history.pushState(
      { ...window.history.state, __authOverlay: true },
      ''
    );

    const onPopState = () => {
      // «Назад»: запись уже снята браузером — просто закрываем оверлей (без повторного back).
      closeAuth(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuth(false);
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('popstate', onPopState);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('popstate', onPopState);
    };
    // Снятие history-записи живёт в closeAuth: он обязан дождаться POP перед тем, как
    // резолвить ожидающих openAuth, иначе асинхронный back() откатывает их навигацию.
  }, [open]);

  const handleSuccess = () => closeAuth(true);

  // Восстановление пароля — отдельная страница, а не режим оверлея: закрываем оверлей
  // и только после снятия его history-записи переходим, передавая введённый телефон.
  const handleForgotPassword = (state?: IAuthPhoneState) => {
    void closeAuth(false).then(() => {
      navigate(`/${ROUTES.resetPassword}`, { state });
    });
  };

  return (
    <div
      className={classNames('auth-overlay', {
        'auth-overlay--visible': open
      })}
      role="dialog"
      aria-modal="true"
      // Не aria-hidden: закрытый оверлей остаётся в DOM, и фокус мог остаться на «×» —
      // тогда браузер ругается «aria-hidden on an element because its descendant retained
      // focus». inert и скрывает от скринридеров, и снимает/запрещает фокус внутри.
      inert={!open}
    >
      <div className="auth-overlay__backdrop" />
      <div className="auth-overlay__sheet">
        <button
          type="button"
          className="auth-overlay__close"
          aria-label="Close"
          onClick={() => closeAuth(false)}
        >
          ×
        </button>

        {/* Оверлей стоит вне ErrorBoundary страниц: без своей границы сбой формы уносил бы
            всё приложение. Новое открытие или смена режима начинают с чистой формы. */}
        <ErrorBoundary
          FallbackComponent={AuthFormError}
          resetKeys={[open, mode]}
        >
          <Suspense fallback={<MainLoader fullWidth />}>
            {mode === 'login' ? (
              <Login
                onSwitch={() => setAuthMode('register')}
                onSuccess={handleSuccess}
                onForgotPassword={handleForgotPassword}
              />
            ) : (
              <Registration
                onSwitch={() => setAuthMode('login')}
                onSuccess={handleSuccess}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default AuthOverlay;
